<script lang="ts">
  import { preventDefault } from "svelte/legacy";

  import { useQueryClient } from "@sveltestack/svelte-query";
  import { useGetProducts } from "../../helpers/api";
  import { getAvaliableProducts } from "../../helpers/helperMethods";
  import { getProductWithId } from "../../helpers/priceCalculator";
  import {
    type PurchaseFlowType,
    type AvaliableProducts,
    type Product,
    type TResponse,
    type Offer,
  } from "../../helpers/types";
  import { onMount } from "svelte";
  import ZipCodeError from "../molecules/ZipCodeError.svelte";
  import Error from "../molecules/Error.svelte";
  import Loader from "../atoms/Loader.svelte";
  import Text from "../atoms/Text.svelte";
  import ProductSelector from "./ProductSelector.svelte";
  import Button from "../atoms/Button.svelte";
  import Card from "../atoms/Card.svelte";
  import Notice from "../atoms/Notice.svelte";

  interface Props {
    estimation: number;
    selectedProductId: number | null;
    selectedProduct: Product | undefined;
    zipcode: string | undefined;
    campaign: string;
    productOrder: string[];
    removeSelectedProductId: () => void;
    goBack: () => void;
    onClickContinue: (e: Event) => void;
    children?: import("svelte").Snippet;
    purchaseFlowType: PurchaseFlowType;
    showSelectProductError?: boolean;
    avaliableProducts?: AvaliableProducts;
    offer?: Offer;
  }

  let {
    estimation,
    selectedProductId = $bindable(),
    selectedProduct = $bindable(),
    zipcode = $bindable(),
    campaign,
    productOrder,
    removeSelectedProductId,
    goBack,
    onClickContinue,
    children,
    purchaseFlowType,
    showSelectProductError = $bindable(false),
    avaliableProducts = $bindable({}),
    offer = $bindable(),
  }: Props = $props();

  const client = useQueryClient();

  // let avaliableProducts: AvaliableProducts = $state({});
  let canContinue = $state(true);

  const parseProducts = (data: TResponse) => {
    avaliableProducts = getAvaliableProducts(
      data,
      estimation,
      productOrder.includes("fixed"),
      productOrder.includes("variable"),
      productOrder.includes("hourly")
    );
    
    // Expose the offer data
    offer = data.result.offer;
    
    if (selectedProductId) {
      let isProduct = getProductWithId(selectedProductId, avaliableProducts);
      if (!isProduct) {
        removeSelectedProductId();
      } else {
        selectedProduct = isProduct;
      }
    }
  };

  const params = $state({
    zipcode: zipcode ?? "",
    code: campaign,
  });

  $effect(() => {
    if (zipcode) {
      params.zipcode = zipcode;
    }
  });

  const queryResult = useGetProducts(params, {
    enabled: false,
    onSuccess: (data) => {
      canContinue = true;
      parseProducts(data);
    },
  });

  if (client.getQueryData(["products", params])) {
    const data = client.getQueryData<TResponse>(["products", params]);
    if (data) {
      parseProducts(data);
    }
  }

  const fetchProducts = () => $queryResult.refetch();

  onMount(() => {
    if (!client.getQueryData(["products", params])) {
      fetchProducts();
    }
  });

  const cardCount = $derived(
    (avaliableProducts.variableProducts?.length ?? 0) +
    (avaliableProducts.hourlyProducts?.length ?? 0) +
    (avaliableProducts.fixedProducts ? 1 : 0)
  );

  $effect(() => {
    if (selectedProduct) {
      showSelectProductError = false;
    }
  });
</script>

{#if $queryResult.status == "error"}
  {#if $queryResult.error && ($queryResult.error as any).result && "zipcode" in ($queryResult.error as any).result}
    <ZipCodeError refetchPrices={fetchProducts} bind:zipcode />
  {:else}
    <Error
      onClick={() => {
        $queryResult.refetch();
      }}
      >Något gick fel vid hämtning av priser
    </Error>
  {/if}
{:else if $queryResult.status == "loading"}
  <Card class="h-110 flex justify-center items-center">
    <Loader />
  </Card>
{:else if $queryResult.status == "success"}
  <form onsubmit={preventDefault(onClickContinue)} class="a">
    <ProductSelector {productOrder} bind:selectedProduct {avaliableProducts}>
      {#if cardCount > 1}
        {@render children?.()}
      {/if}
    </ProductSelector>
    {#if showSelectProductError}
      <Notice tone="error" class="mb-3">För att fortsätta måste du först välja ett elpris</Notice>
    {/if}
    <Button
      class="w-full mb-3 mt-2"
      type="submit"
      animate={false}
      disabled={purchaseFlowType == "v1" ? !selectedProduct || !canContinue : false}
      >Fortsätt</Button
    >
  </form>
  <div class="flex flex-col gap-2">
    <Text size="S" variant="secondary" class="leading-5">
      Baserat på en förbrukning på {estimation} kWh/år för postnummer {zipcode}.
      <button
      type="button"
      onclick={() => {
        goBack();
      }}
      class=" text-Violet700 hover:underline">Ändra uppgifter.</button
    >
      Din slutliga månadskostnad kommer att beräknas på din faktiska elanvändning. Elnätskostnad tillkommer.
    </Text>
    <Text size="S" variant="secondary" class="leading-5">
      Läs här om
      <a
        href="https://www.skekraft.se/kundservice/konsumentrattigheter/"
        target="_blank"
        class="text-Violet700 hover:underline">dina konsumenträttigheter</a
      >.
    </Text>
  </div>
{/if}
